import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Snackbar,
    useTheme,
    useMediaQuery,
    Stack,
    CardActions,
    Divider
} from '@mui/material';
import {
    VerifiedUser as LicenseIcon,
    Warning as WarningIcon,
    CheckCircle as ChecklistIcon,
    Report as IncidentIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getComplianceSummary, getExpiringLicenses } from '../../network/apis/compliance.api';
import { useToast } from '../../hooks/useToast';

const ComplianceDashboard = () => {
    const { currentOrganization, userRole } = useOrganization();
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const { toast, showError, hideToast } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [summary, setSummary] = useState(null);
    const [expiringLicenses, setExpiringLicenses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Only managers+ can access
    const canAccess = ['manager', 'admin', 'owner'].includes(userRole?.toLowerCase());

    useEffect(() => {
        if (currentOrganization?.organizationId && canAccess) {
            fetchDashboardData();
        }
    }, [currentOrganization, canAccess]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            const [summaryData, expiringData] = await Promise.all([
                getComplianceSummary(token),
                getExpiringLicenses(token)
            ]);
            setSummary(summaryData);
            setExpiringLicenses(expiringData.expiringLicenses || []);
        } catch (error) {
            showError('Failed to load compliance data');
        } finally {
            setLoading(false);
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'Critical': return 'error';
            case 'Major': return 'warning';
            case 'Moderate': return 'info';
            case 'Minor': return 'success';
            default: return 'default';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const getDaysUntilExpiry = (expiryDate) => {
        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    if (!canAccess) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">
                    Access Denied. Only Managers and above can view the Compliance Dashboard.
                </Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography
                variant="h4"
                gutterBottom
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
            >
                Compliance Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                Monitor pilot licenses, checklists, and incident reports
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                            <Typography color="text.secondary" variant="body2" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', mb: isMobile ? 0.5 : 1 }}>
                                Active Licenses
                            </Typography>
                            <Typography variant="h4" sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
                                {summary?.activeLicenses || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                            <Typography color="text.secondary" variant="body2" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', mb: isMobile ? 0.5 : 1 }}>
                                Expiring Soon
                            </Typography>
                            <Typography variant="h4" color="warning.main" sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
                                {summary?.expiringLicenses || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                                Next 30 days
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                            <Typography color="text.secondary" variant="body2" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', mb: isMobile ? 0.5 : 1 }}>
                                Checklists
                            </Typography>
                            <Typography variant="h4" sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
                                {summary?.checklistsThisMonth || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                                This month
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                    <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                            <Typography color="text.secondary" variant="body2" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', mb: isMobile ? 0.5 : 1 }}>
                                Incidents
                            </Typography>
                            <Typography variant="h4" color="error.main" sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
                                {summary?.openIncidents?.reduce((sum, item) => sum + parseInt(item.count), 0) || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Expiring Licenses Table/Cards */}
            {expiringLicenses.length > 0 && (
                <Paper sx={{ mb: 3 }}>
                    <Box p={isMobile ? 1.5 : 2}>
                        <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                            Licenses Expiring Soon
                        </Typography>
                        {isMobile ? (
                            // Mobile Card View
                            <Stack spacing={1.5}>
                                {expiringLicenses.map((license) => {
                                    const daysRemaining = getDaysUntilExpiry(license.expiryDate);
                                    return (
                                        <Card key={license.id} variant="outlined">
                                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                {/* Header */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                                            {license.user?.firstName} {license.user?.lastName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                            {license.licenseType}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={`${daysRemaining} days`}
                                                        color={daysRemaining < 7 ? 'error' : daysRemaining < 14 ? 'warning' : 'default'}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '0.7rem' }}
                                                    />
                                                </Box>

                                                {/* Details */}
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                            License #
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                                            {license.licenseNumber}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                            Expires
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
                                                            {formatDate(license.expiryDate)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </CardContent>

                                            {/* Actions */}
                                            <CardActions sx={{ p: 1, pt: 0, gap: 0.5, justifyContent: 'flex-end' }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate('/compliance/licenses')}
                                                    sx={{ padding: '4px' }}
                                                >
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </CardActions>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        ) : (
                            // Desktop Table View
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Pilot</TableCell>
                                            <TableCell>License Number</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Expiry Date</TableCell>
                                            <TableCell>Days Remaining</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {expiringLicenses.map((license) => {
                                            const daysRemaining = getDaysUntilExpiry(license.expiryDate);
                                            return (
                                                <TableRow key={license.id}>
                                                    <TableCell>
                                                        {license.user?.firstName} {license.user?.lastName}
                                                    </TableCell>
                                                    <TableCell>{license.licenseNumber}</TableCell>
                                                    <TableCell>{license.licenseType}</TableCell>
                                                    <TableCell>{formatDate(license.expiryDate)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={`${daysRemaining} days`}
                                                            color={daysRemaining < 7 ? 'error' : daysRemaining < 14 ? 'warning' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="View Licenses">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => navigate('/compliance/licenses')}
                                                            >
                                                                <ViewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            )}

            {/* Open Incidents by Severity */}
            {summary?.openIncidents && summary.openIncidents.length > 0 && (
                <Paper sx={{ mb: 3 }}>
                    <Box p={isMobile ? 1.5 : 2}>
                        <Typography variant="h6" gutterBottom sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                            Open Incidents by Severity
                        </Typography>
                        <Grid container spacing={isMobile ? 1.5 : 2}>
                            {summary.openIncidents.map((incident) => (
                                <Grid item xs={6} sm={6} md={3} key={incident.severity} sx={{ display: 'flex' }}>
                                    <Card variant="outlined" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                                                {incident.severity}
                                            </Typography>
                                            <Typography variant="h5" sx={{ fontSize: isMobile ? '1.5rem' : '1.5rem' }}>
                                                {incident.count}
                                            </Typography>
                                            <Chip
                                                label={incident.severity}
                                                color={getSeverityColor(incident.severity)}
                                                size="small"
                                                sx={{ mt: isMobile ? 0.5 : 1, height: isMobile ? 20 : 24, fontSize: isMobile ? '0.7rem' : '0.8125rem' }}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Paper>
            )}

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={hideToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={hideToast} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ComplianceDashboard;
